import React, { useContext, useEffect, useState, useRef } from 'react';
import styles from './GameInfo.module.css';
import { GameContext } from '../Providers/GameContext';
import { useTheme } from '../Providers/ThemeContext';
import { useLanguage } from '../Providers/LanguageContext';
import { Mode, MoveType, PieceType } from '../../services/zodiacEngine/types';

// Define capture restriction rules (what this piece CANNOT capture)
// Note: These are TRUE capture restrictions, not movement/entangle/infect restrictions
const captureRestrictions: Record<PieceType, PieceType[]> = {
  [PieceType.Crane]: [PieceType.Bear, PieceType.Rhino, PieceType.Tree, PieceType.Wolf],  // Cannot capture Large pieces
  [PieceType.Goat]: [PieceType.Bear, PieceType.Wolf],  // Cannot capture Predators
  [PieceType.Penguin]: [PieceType.Bear, PieceType.Rhino, PieceType.Tree, PieceType.Wolf],  // Cannot capture Large pieces
  [PieceType.Virus]: [PieceType.Tree, PieceType.Fungi],  // Can only infect animals (displayed as "cannot infect")
  [PieceType.Monkey]: [PieceType.Bear], // Special case - can capture but needs to roll 5 or 6
  // Other pieces don't have capture restrictions
  [PieceType.Bear]: [],
  [PieceType.Frog]: [],
  [PieceType.Fungi]: [],
  [PieceType.Rhino]: [],
  [PieceType.Snake]: [],
  [PieceType.Wolf]: [],
  [PieceType.Tree]: [],  // Meadow (immobilization by Fungi) is movement restriction, not capture
  [PieceType.Octopus]: [],  // Entangle restrictions are separate from capture (Octopus CAN capture orthogonally)
};

// Entangle restrictions (Octopus cannot entangle plants or virus)
const entangleRestrictions: Partial<Record<PieceType, PieceType[]>> = {
  [PieceType.Octopus]: [PieceType.Tree, PieceType.Fungi, PieceType.Virus],  // Cannot entangle plants or virus
};

// Meadow restriction (Tree is immobilized by Fungi)
const meadowRestriction = {
  affected: PieceType.Tree,
  causedBy: PieceType.Fungi,
};

// Build reverse mapping: what pieces CANNOT capture this piece type
// This is derived from captureRestrictions - if Crane cannot capture Bear,
// then Bear cannot be captured by Crane
const immuneFrom: Record<PieceType, PieceType[]> = Object.keys(captureRestrictions).reduce(
  (acc, pieceType) => {
    acc[pieceType as PieceType] = [];
    return acc;
  },
  {} as Record<PieceType, PieceType[]>
);

// Populate immuneFrom by inverting captureRestrictions
for (const [attacker, targets] of Object.entries(captureRestrictions)) {
  for (const target of targets) {
    if (!immuneFrom[target].includes(attacker as PieceType)) {
      immuneFrom[target].push(attacker as PieceType);
    }
  }
}

const GameInfo: React.FC = () => {
  const { selectedPiece, isLightPlayer, isThisPlayersTurn, selectionLocked, potentialMoves, gameState, isObserveMode } = useContext(GameContext)!;
  const { theme } = useTheme();
  const { t } = useLanguage();
  const [showInfo, setShowInfo] = useState(false);
  const [isMonkeyBonusTurn, setIsMonkeyBonusTurn] = useState(false);
  const [isVirusBonusTurn, setIsVirusBonusTurn] = useState(false);
  const [isPenguinBonusTurn, setIsPenguinBonusTurn] = useState(false);
  const [uncapturablePieces, setUncapturablePieces] = useState<PieceType[]>([]);
  const [immuneFromPieces, setImmuneFromPieces] = useState<PieceType[]>([]);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const previousSelectionLocked = useRef(false);
  const previousTurnCount = useRef(gameState.turnCount);

  // Check for dark mode from theme context or DOM
  useEffect(() => {
    if (theme) {
      // Use theme context if available (ChallengeAI page)
      setIsDarkMode(theme === 'dark');
    } else {
      // Fall back to DOM observation (BoardContainer page)
      const checkDarkMode = () => {
        const darkElement = document.querySelector('.dark');
        setIsDarkMode(darkElement !== null);
      };
      
      checkDarkMode();
      
      const observer = new MutationObserver(checkDarkMode);
      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ['class'],
        subtree: true
      });
      
      return () => observer.disconnect();
    }
  }, [theme]);

  // Track selection locked state to detect bonus turn
  useEffect(() => {
    

    // If selection just became locked and the piece is a Monkey, it's likely a swing
    if (selectionLocked && !previousSelectionLocked.current && selectedPiece?.piece.type === 'Monkey') {
      
      setIsMonkeyBonusTurn(true);
      setIsVirusBonusTurn(false);
      setIsPenguinBonusTurn(false);
    }
    
    // If selection just became locked and the piece is a Virus, it's a virus chain move
    if (selectionLocked && !previousSelectionLocked.current && selectedPiece?.piece.type === 'Virus') {
      
      setIsVirusBonusTurn(true);
      setIsMonkeyBonusTurn(false);
      setIsPenguinBonusTurn(false);
    }
    
    // If selection just became locked and the piece is a Penguin, it's a penguin bonus turn
    if (selectionLocked && !previousSelectionLocked.current && selectedPiece?.piece.type === 'Penguin') {
      
      setIsPenguinBonusTurn(true);
      setIsMonkeyBonusTurn(false);
      setIsVirusBonusTurn(false);
    }
    
    // If selection became unlocked, reset the bonus turn states
    if (!selectionLocked && previousSelectionLocked.current) {
     
      setIsMonkeyBonusTurn(false);
      setIsVirusBonusTurn(false);
      setIsPenguinBonusTurn(false);
    }
    
    // Update ref for next check
    previousSelectionLocked.current = selectionLocked;
  }, [selectionLocked, selectedPiece]);

  // Function to find enemy pieces on the board
  const getEnemyPiecesOnBoard = () => {
    const enemyPieces: PieceType[] = [];
    
    // Iterate the board to find all enemy pieces
    for (let row = 0; row < gameState.board.length; row++) {
      for (let col = 0; col < gameState.board[row].length; col++) {
        const square = gameState.board[row][col];
        if (square.Pieces && square.Pieces.length > 0) {
          // Check each piece on this square
          for (const piece of square.Pieces) {
            if (isEnemyPiece(piece) && !enemyPieces.includes(piece.type)) {
              enemyPieces.push(piece.type);
            }
          }
        }
      }
    }
    
    return enemyPieces;
  };

  // Function to determine which enemy pieces the selected piece cannot capture/affect
  const getUncapturablePieces = () => {
    if (!selectedPiece) return [];

    const selectedPieceType = selectedPiece.piece.type as PieceType;

    // Get capture restrictions
    let restrictions = captureRestrictions[selectedPieceType] || [];

    // For Octopus, also include entangle restrictions
    if (selectedPieceType === PieceType.Octopus) {
      restrictions = entangleRestrictions[PieceType.Octopus] || [];
    }

    // Only get restrictions that apply to enemy pieces currently on the board
    const enemyPieces = getEnemyPiecesOnBoard();
    return restrictions.filter(pieceType => enemyPieces.includes(pieceType));
  };

  // Check if Tree is affected by Meadow (Fungi nearby)
  const isTreeMeadowed = () => {
    if (!selectedPiece) return false;
    if (selectedPiece.piece.type !== PieceType.Tree) return false;

    // Check if there's an enemy Fungi on the board
    const enemyPieces = getEnemyPiecesOnBoard();
    return enemyPieces.includes(PieceType.Fungi);
  };

  // Function to determine which enemy pieces CANNOT capture the selected piece
  const getImmuneFromPieces = () => {
    if (!selectedPiece) return [];

    const selectedPieceType = selectedPiece.piece.type as PieceType;
    const immunities = immuneFrom[selectedPieceType] || [];

    // Only get immunities that apply to enemy pieces currently on the board
    const enemyPieces = getEnemyPiecesOnBoard();
    return immunities.filter(pieceType => enemyPieces.includes(pieceType));
  };

  // Handle showing/hiding info and update uncapturable pieces info
  useEffect(() => {
    // Check if turnCount changed (move completed)
    const moveCompleted = gameState.turnCount !== previousTurnCount.current;
    previousTurnCount.current = gameState.turnCount;

    if (moveCompleted && !selectionLocked) {
      setShowInfo(false);
      setUncapturablePieces([]);
      setImmuneFromPieces([]);
      return;
    }

    // Show info when a piece is selected with valid moves, or during a bonus turn
    const shouldShowInfo = (selectedPiece !== undefined && potentialMoves.length > 0) ||
      (selectionLocked && (isMonkeyBonusTurn || isVirusBonusTurn || isPenguinBonusTurn));

    setShowInfo(shouldShowInfo);

    // If showing info and a piece is selected, update uncapturable pieces and immune from pieces
    // In Sandbox/TestAI mode, show info for all pieces since you control both sides
    if (shouldShowInfo && selectedPiece) {
      const isOwnPieceOrBothSidesControlled = !isEnemyPiece(selectedPiece.piece) ||
                                              gameState.mode === Mode.Sandbox ||
                                              gameState.mode === Mode.TestAI;
      if (isOwnPieceOrBothSidesControlled) {
        setUncapturablePieces(getUncapturablePieces());
        setImmuneFromPieces(getImmuneFromPieces());
      } else {
        setUncapturablePieces([]);
        setImmuneFromPieces([]);
      }
    } else {
      setUncapturablePieces([]);
      setImmuneFromPieces([]);
    }

  }, [selectedPiece, potentialMoves, gameState.turnCount, selectionLocked, isMonkeyBonusTurn, isVirusBonusTurn, isPenguinBonusTurn]);
  
  const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  // Helper to check if a piece is an enemy piece
  const isEnemyPiece = (piece: any) => piece.isLight !== isLightPlayer();

  // Render the capture restriction information for the selected piece
  const getCaptureRestrictionInfo = () => {
    if (!selectedPiece || uncapturablePieces.length === 0) return null;

    const pieceType = selectedPiece.piece.type;
    const translatedPieceName = t(`pieces.${pieceType}`);

    // Only show for own pieces, not enemy pieces (except in Sandbox/TestAI where you control both)
    if (isEnemyPiece(selectedPiece.piece) && gameState.mode !== Mode.Sandbox && gameState.mode !== Mode.TestAI) return null;

    // Special case for Monkey vs Bear
    if (pieceType === 'Monkey' && uncapturablePieces.includes(PieceType.Bear)) {
      return (
        <div className={styles.captureRestriction}>
          <span>{t('gameInfo.monkeyBearRoll', { piece: translatedPieceName, target: t('pieces.Bear') })}</span>
        </div>
      );
    }

    // Special case for Virus
    if (pieceType === 'Virus' && uncapturablePieces.length > 0) {
      const pieces = uncapturablePieces.map(p => t(`pieces.${p}`)).join(', ');
      return (
        <div className={styles.captureRestriction}>
          <span>{t('gameInfo.cannotInfect', { piece: translatedPieceName, targets: pieces })}</span>
        </div>
      );
    }

    // Special case for Octopus
    if (pieceType === 'Octopus' && uncapturablePieces.length > 0) {
      const pieces = uncapturablePieces.map(p => t(`pieces.${p}`)).join(', ');
      return (
        <div className={styles.captureRestriction}>
          <span>{t('gameInfo.cannotEntangle', { piece: translatedPieceName, targets: pieces })}</span>
        </div>
      );
    }

    // Standard case
    const pieces = uncapturablePieces.map(p => t(`pieces.${p}`)).join(', ');
    return (
      <div className={styles.captureRestriction}>
        <span>{t('gameInfo.cannotCapture', { piece: translatedPieceName, targets: pieces })}</span>
      </div>
    );
  };

  // Render meadow warning for Tree when Fungi is on the board
  const getMeadowWarning = () => {
    if (!selectedPiece) return null;
    if (selectedPiece.piece.type !== PieceType.Tree) return null;

    // Only show for own pieces
    if (isEnemyPiece(selectedPiece.piece) && gameState.mode !== Mode.Sandbox && gameState.mode !== Mode.TestAI) return null;

    // Check if there's an enemy Fungi on the board
    const enemyPieces = getEnemyPiecesOnBoard();
    if (!enemyPieces.includes(PieceType.Fungi)) return null;

    const translatedPieceName = t(`pieces.${PieceType.Tree}`);
    const fungiName = t(`pieces.${PieceType.Fungi}`);

    return (
      <div className={styles.meadowWarning}>
        <span>{t('gameInfo.meadowWarning', { piece: translatedPieceName, fungi: fungiName })}</span>
      </div>
    );
  };

  // Render Rhino charge warning when Fungi, Tree, or Virus are on the board
  const getRhinoChargeWarning = () => {
    if (!selectedPiece) return null;
    if (selectedPiece.piece.type !== PieceType.Rhino) return null;

    // Only show for own pieces
    if (isEnemyPiece(selectedPiece.piece) && gameState.mode !== Mode.Sandbox && gameState.mode !== Mode.TestAI) return null;

    // Check for enemy Fungi, Tree, or Virus on the board
    const enemyPieces = getEnemyPiecesOnBoard();
    const chargeImmunePieces = [PieceType.Fungi, PieceType.Tree, PieceType.Virus];
    const presentImmunePieces = chargeImmunePieces.filter(p => enemyPieces.includes(p));

    if (presentImmunePieces.length === 0) return null;

    const translatedPieceName = t(`pieces.${PieceType.Rhino}`);
    const targetNames = presentImmunePieces.map(p => t(`pieces.${p}`)).join(', ');

    return (
      <div className={styles.meadowWarning}>
        <span>{t('gameInfo.rhinoChargeWarning', { piece: translatedPieceName, targets: targetNames })}</span>
      </div>
    );
  };

  // Render Snake strike warning when Fungi, Tree, or Virus are on the board
  const getSnakeStrikeWarning = () => {
    if (!selectedPiece) return null;
    if (selectedPiece.piece.type !== PieceType.Snake) return null;

    // Only show for own pieces
    if (isEnemyPiece(selectedPiece.piece) && gameState.mode !== Mode.Sandbox && gameState.mode !== Mode.TestAI) return null;

    // Check for enemy Fungi, Tree, or Virus on the board
    const enemyPieces = getEnemyPiecesOnBoard();
    const strikeImmunePieces = [PieceType.Fungi, PieceType.Tree, PieceType.Virus];
    const presentImmunePieces = strikeImmunePieces.filter(p => enemyPieces.includes(p));

    if (presentImmunePieces.length === 0) return null;

    const translatedPieceName = t(`pieces.${PieceType.Snake}`);
    const targetNames = presentImmunePieces.map(p => t(`pieces.${p}`)).join(', ');

    return (
      <div className={styles.meadowWarning}>
        <span>{t('gameInfo.snakeStrikeWarning', { piece: translatedPieceName, targets: targetNames })}</span>
      </div>
    );
  };

  // Render the "cannot be captured by" information for the selected piece
  const getImmunityInfo = () => {
    if (!selectedPiece || immuneFromPieces.length === 0) return null;

    const pieceType = selectedPiece.piece.type;
    const translatedPieceName = t(`pieces.${pieceType}`);

    // Only show for own pieces, not enemy pieces (except in Sandbox/TestAI where you control both)
    if (isEnemyPiece(selectedPiece.piece) && gameState.mode !== Mode.Sandbox && gameState.mode !== Mode.TestAI) return null;

    // Special case for Bear - Monkey can capture but needs to roll
    if (pieceType === 'Bear' && immuneFromPieces.includes(PieceType.Monkey)) {
      // Filter out Monkey from the list and handle separately
      const otherImmune = immuneFromPieces.filter(p => p !== PieceType.Monkey);
      const monkeyNote = t('gameInfo.monkeyNeedsRoll', { attacker: t('pieces.Monkey') });

      if (otherImmune.length > 0) {
        const pieces = otherImmune.map(p => t(`pieces.${p}`)).join(', ');
        return (
          <div className={styles.immunityInfo}>
            <span>{t('gameInfo.cannotBeCapturedBy', { piece: translatedPieceName, attackers: pieces })}</span>
            <span className={styles.immunityNote}>{monkeyNote}</span>
          </div>
        );
      } else {
        return (
          <div className={styles.immunityInfo}>
            <span className={styles.immunityNote}>{monkeyNote}</span>
          </div>
        );
      }
    }

    // Standard case
    const pieces = immuneFromPieces.map(p => t(`pieces.${p}`)).join(', ');
    return (
      <div className={styles.immunityInfo}>
        <span>{t('gameInfo.cannotBeCapturedBy', { piece: translatedPieceName, attackers: pieces })}</span>
      </div>
    );
  };

  const getInfoContent = () => {
    if (!selectedPiece) return null;

    const pieceType = selectedPiece.piece.type;
    const translatedPieceName = t(`pieces.${pieceType}`);
    const isEnemyPieceSelected = isEnemyPiece(selectedPiece.piece);
    const isOwnGoat = !isEnemyPieceSelected && pieceType === 'Goat';
    const isOwnPenguin = !isEnemyPieceSelected && pieceType === 'Penguin';
    const isOwnOctopus = !isEnemyPieceSelected && pieceType === 'Octopus';
    const isOwnVirus = !isEnemyPieceSelected && pieceType === 'Virus';

    if (isMonkeyBonusTurn && selectedPiece.piece.type === 'Monkey') {
      // Check the actual move type that was just completed
      const lastMove = (gameState as any).lastMove as { moveType: string } | undefined;
      const lastMoveWasConsume = lastMove?.moveType === MoveType.consume;

      return (
        <div className={styles.infoItem}>
          <span className={styles.selectionText}>
            {t('gameInfo.selectionLocked', { piece: translatedPieceName })}
          </span>
          <span className={styles.hintText}>
            {lastMoveWasConsume ? t('gameInfo.monkeyConsume') : t('gameInfo.monkeySwing')}
          </span>
          {getCaptureRestrictionInfo()}
          {getImmunityInfo()}
        </div>
      );
    } else if (isVirusBonusTurn && selectedPiece.piece.type === 'Virus') {
      return (
        <div className={styles.infoItem}>
          <span className={styles.selectionText}>
            {t('gameInfo.selectionLocked', { piece: translatedPieceName })}
          </span>
          <span className={styles.hintText}>
            {t('gameInfo.virusInfecting')}
          </span>
          {getCaptureRestrictionInfo()}
          {getImmunityInfo()}
        </div>
      );
    } else if (isPenguinBonusTurn && selectedPiece.piece.type === 'Penguin') {
      // Check the actual move type that was just completed
      const lastMove = (gameState as any).lastMove as { moveType: string } | undefined;
      const lastMoveWasCapture = lastMove?.moveType === MoveType.capture;
      const isZombified = selectedPiece.piece.zombifiedStatus?.isZombified;

      return (
        <div className={styles.infoItem}>
          <span className={styles.selectionText}>
            {t('gameInfo.selectionLockedPenguin')}
          </span>
          <span className={styles.hintText}>
            {isZombified
              ? t('gameInfo.penguinZombified')
              : (lastMoveWasCapture
                  ? t('gameInfo.penguinCaptureRotate')
                  : t('gameInfo.penguinRotate'))}
          </span>
          {getCaptureRestrictionInfo()}
          {getImmunityInfo()}
        </div>
      );
    } else {
      return (
        <div className={styles.infoItem}>
          <span className={styles.selectionText}>
            {isObserveMode
              ? (selectedPiece.piece.zombifiedStatus?.isZombified
                  ? t('gameInfo.selectingZombified', { piece: translatedPieceName })
                  : t('gameInfo.observing', { piece: translatedPieceName }))
              : t('gameInfo.selecting', { piece: translatedPieceName })}
          </span>
          {isOwnGoat && (
            <span className={styles.hintText}>
              {t('gameInfo.clickToFaint', { piece: translatedPieceName })}
            </span>
          )}
          {isOwnPenguin && selectedPiece.piece.zombifiedStatus?.isZombified && (
            <span className={styles.hintText}>
              {t('gameInfo.zombifiedWarning', { piece: translatedPieceName })}
            </span>
          )}
          {isOwnPenguin && !selectedPiece.piece.zombifiedStatus?.isZombified && (
            <span className={styles.hintText}>
              {t('gameInfo.clickToRotate', { piece: translatedPieceName })}
            </span>
          )}
          {isOwnOctopus && (
            <span className={styles.hintText}>
              {t('gameInfo.clickToInk', { piece: translatedPieceName })}
            </span>
          )}
          {/* Show capture restrictions for own pieces, or all pieces in Sandbox/TestAI */}
          {(!isEnemyPieceSelected || gameState.mode === Mode.Sandbox || gameState.mode === Mode.TestAI) && getCaptureRestrictionInfo()}
          {/* Show meadow warning for Tree when Fungi is present */}
          {(!isEnemyPieceSelected || gameState.mode === Mode.Sandbox || gameState.mode === Mode.TestAI) && getMeadowWarning()}
          {/* Show Rhino charge warning when Fungi/Tree/Virus are present */}
          {(!isEnemyPieceSelected || gameState.mode === Mode.Sandbox || gameState.mode === Mode.TestAI) && getRhinoChargeWarning()}
          {/* Show Snake strike warning when Fungi/Tree/Virus are present */}
          {(!isEnemyPieceSelected || gameState.mode === Mode.Sandbox || gameState.mode === Mode.TestAI) && getSnakeStrikeWarning()}
          {/* Show immunity info (what can't capture this piece) for own pieces */}
          {(!isEnemyPieceSelected || gameState.mode === Mode.Sandbox || gameState.mode === Mode.TestAI) && getImmunityInfo()}
        </div>
      );
    }
  };

  const getTurnStatus = () => {
    // Sandbox mode has no turns - show special message
    if (gameState.mode === Mode.Sandbox) {
      return (
        <div className={`${styles.turnStatus} ${styles.yourTurn}`}>
          {t('gameInfo.sandboxMode')}
        </div>
      );
    }

    // TestAI mode = local 2-player, both sides controlled by player
    if (gameState.mode === Mode.TestAI) {
      return (
        <div className={`${styles.turnStatus} ${styles.yourTurn}`}>
          {gameState.isLightTurn ? t('gameInfo.lightTurn') : t('gameInfo.darkTurn')}
        </div>
      );
    }

    const isYourTurn = isThisPlayersTurn();
    const isAIMode = gameState.mode === Mode.AI && !isThisPlayersTurn();

    return (
      <div className={`${styles.turnStatus} ${isYourTurn ? styles.yourTurn : styles.opponentsTurn}`}>
        {isYourTurn
          ? t('gameInfo.yourTurn')
          : isAIMode
            ? t('gameInfo.aiThinking')
            : t('gameInfo.opponentTurn')
        }
      </div>
    );
  };

  return (
    <div className={`${styles.gameInfoContainer} ${isDarkMode ? styles.dark : ''}`}>
      <h3>{t('gameInfo.title')}</h3>
      <div className={styles.contentBox}>
        {showInfo && getInfoContent()}
      </div>
      {getTurnStatus()}
    </div>
  );
};

export default GameInfo; 